<template>
  <div class="d-flex align-center ml-2 mb-2">
    <span class="text-h6">{{ $t('HOME.MY_ACTIONS') }}</span>
    <v-tooltip max-width="250" content-class="line-height-normal">
      <template #activator="{ props }">
        <v-icon color="info" size="small" class="ml-2" v-bind="props">far fa-question-circle</v-icon>
      </template>
      <span>{{ $t('HOME.ACTIONS_EXPLANATIONS') }}</span>
    </v-tooltip>
  </div>
  <div>
    <v-card v-if="! loaded" class="flex-center-center" style="font-size:18px; height: 240px"><i class="far fa-spinner fa-spin fa-2x fa-fw"></i></v-card>
    <v-card v-else-if="! hasActions">
      <!-- empty state -->
      <v-tabs disabled grow density="comfortable">
        <v-tab value="chart" variant="tonal">
          <v-icon size="large" class="far fa-chart-pie mr-2"></v-icon>
          <div class="align-self-baseline">0</div>
        </v-tab>
        <v-tab v-for="tab in tabs" :key="tab.id" :value="tab.id" variant="tonal">
          <v-icon size="large" :class="tab.icon" class="mr-2"></v-icon>
          <div class="align-self-baseline">0</div>
        </v-tab>
      </v-tabs>
      <div class="px-4 d-flex gap-4 align-center justify-center text-medium-emphasis" style="min-height: max(calc(50vh - 96px - 44px), 196px)">
        <img src="./emptychart.svg" style="height: 180px; max-width: 40%">
        <div style="max-width: 600px">
          <div class="mb-2 font-weight-black">{{ $t('HOME.NO_ACTIVITY') }}</div>
          <div>{{ $t('HOME.FIND_HERE') }} {{ $t('HOME.ACTIONS_EXPLANATIONS').charAt(0).toLowerCase() }}{{ $t('HOME.ACTIONS_EXPLANATIONS').slice(1) }}</div>
        </div>
      </div>
    </v-card>
    <v-card v-else>
      <v-tabs v-model="selectedTab" grow density="comfortable" mandatory>
        <v-tab value="chart" variant="tonal" base-color="primary">
          <v-icon size="large" class="far fa-chart-pie mr-2"></v-icon>
          <div class="align-self-baseline">{{ totalActions }}</div>
        </v-tab>
        <v-tab v-for="tab in tabs" :key="tab.id" :value="tab.id" variant="tonal"
               :title="tab.title" :base-color="tab.color" :disabled="! actionsByStatus[tab.id].length">
          <v-icon size="large" :class="tab.icon" class="mr-2"></v-icon>
          <div class="align-self-baseline">{{ actionsCountByStatus[tab.id] }}</div>
        </v-tab>
      </v-tabs>
      <v-window v-model="selectedTab">
        <v-window-item value="chart">
          <btn-toggle-filter v-model="onlyMyActions" mandatory="force" variant="outlined" density="compact" style="position: absolute; top: 4px; right: 8px">
            <v-btn :value="false" class="px-3">
              <v-icon style="width: 24px">far fa-building</v-icon>
              <v-tooltip location="bottom" activator="parent">{{ $t('PLANNINGDRAWER.ALL_ACTIONS') }}</v-tooltip>
            </v-btn>
            <v-btn :value="true" class="px-3">
              <user-avatar :user="me" :size="24"></user-avatar>
              <v-tooltip location="bottom" activator="parent">{{ $t('PLANNINGDRAWER.ONLY_MY_ACTIONS') }}</v-tooltip>
            </v-btn>
          </btn-toggle-filter>
          <div class="px-4 d-flex gap-4 align-center justify-center" style="min-height: calc(50vh - 96px - 44px); height: 196px">
            <div v-if="! totalActions" class="small text-medium-emphasis">{{ $t('PLANNINGDRAWER.NO_ACTIONS') }}</div>
            <template v-else>
              <chart-js :config="actionsChart" :width="180" :height="180"></chart-js>
              <div>
                <div v-for="tab in tabs">
                  <v-btn variant="text" :color="tab.color" :disabled="! filteredActionsByStatus[tab.id].length" @click="selectedTab = tab.id">
                    <div class="mr-2" style="width: 40px; height: 12px; background: currentColor;"></div>
                    {{ tab.title }}
                  </v-btn>
                </div>
              </div>
            </template>
          </div>
        </v-window-item>
        <v-window-item v-for="tab in tabs" :key="tab.id" :value="tab.id">
          <div class="py-1 overflow-y-auto overflow-x-hidden light-scrollbar" style="min-height: calc(50vh - 96px - 44px); height: 240px">
            <btn-toggle-filter v-model="onlyMyActions" mandatory="force" variant="outlined" density="compact" class="mx-2 float-right">
              <v-btn :value="false" class="px-3">
                <v-icon style="width: 24px">far fa-building</v-icon>
                <v-tooltip location="bottom" activator="parent">{{ $t('PLANNINGDRAWER.ALL_ACTIONS') }}</v-tooltip>
              </v-btn>
              <v-btn :value="true" class="px-3">
                <user-avatar :user="me" :size="24"></user-avatar>
                <v-tooltip location="bottom" activator="parent">{{ $t('PLANNINGDRAWER.ONLY_MY_ACTIONS') }}</v-tooltip>
              </v-btn>
            </btn-toggle-filter>
            <div v-if="! filteredActionsByStatus[tab.id].length" class="small text-medium-emphasis mt-2 mx-4">{{ $t('PLANNINGDRAWER.NO_ACTIONS') }}</div>
            <home-user-actions-list v-else :actions="filteredActionsByStatus[tab.id]"></home-user-actions-list>
          </div>
        </v-window-item>
      </v-window>
    </v-card>
  </div>
</template>

<script>
  import { mapState } from 'pinia';
  import useHomeUserStore from '@/stores/home-user';
  import useUsersStore from '@/stores/users';
  import HomeUserActionsList from './HomeUserActionsList.vue';
  import ChartJs from '@/components/Reusables/ChartJs.vue';

  export default {
    components: {
      HomeUserActionsList,
      ChartJs,
    },
    data() {
      return {
        tabs: [
          { id: 'late', title: this.$t("HOME.LATE"), color: 'errorred', icon: 'far fa-exclamation-triangle' },
          { id: 'urgent', title: this.$t("HOME.URGENT"), color: 'warningorange', icon: 'far fa-clock' },
          { id: 'current', title: this.$t("HOME.CURRENT"), color: 'primary', icon: 'far fa-hourglass-half' },
        ],
        selectedTab: 'chart',
        onlyMyActions: false,
        usersStore: useUsersStore(),
      };
    },
    computed: {
      hasActions() {
        return this.tabs.some(tab => this.actionsByStatus[tab.id].length);
      },
      filteredActionsByStatus() {
        if (! this.onlyMyActions) return this.actionsByStatus;
        return this.tabs.reduce((acc, tab) => {
          acc[tab.id] = this.actionsByStatus[tab.id].filter(action => action.getUserId() == this.me.id);
          return acc;
        }, {});
      },
      actionsCountByStatus() {
        return this.tabs.reduce((acc, tab) => { acc[tab.id] = this.filteredActionsByStatus[tab.id].filter(action => ! action.getChecked()).length; return acc; }, {});
      },
      totalActions() {
        return this.tabs.reduce((total, tab) => this.actionsCountByStatus[tab.id] + total, 0);
      },
      actionsChart() {
        if (this.selectedTab != 'chart') return null; // redraw only when visible
        return {
          type: 'doughnut',
          data: {
            labels: this.tabs.map(tab => tab.title),
            datasets: [{
              data: this.tabs.map(tab => this.actionsCountByStatus[tab.id]),
              total: this.totalActions,
              backgroundColor: this.tabs.map(tab => this.$vuetify.theme.current.colors[tab.color]),
              borderWidth: 0,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label(context) {
                    const percent = Math.round(context.parsed / context.dataset.total * 100);
                    return percent ? ` ${percent}%` : '';
                  },
                },
              },
              datalabels: {
                color: 'white',
                formatter(value) {
                  return value || '';
                },
              },
            },
            onClick: (event, elements) => {
              if (elements.length) {
                this.selectedTab = this.tabs[elements[0].index].id;
              }
            },
          },
        };
      },
      me() { return this.usersStore.user; },
      ...mapState(useHomeUserStore, ['actionsByStatus', 'loaded']),
    },
  };
</script>
